import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionTeardownService } from '@backbase/shared/feature/auth';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@backbase/shared/feature/config';
import { SafeLogger } from '@backbase/shared/util/app-core';
import CryptoJS from 'crypto-js';

export interface DecryptedAccountData {
    balance: string;
    fundShareClassCode: string;
    fundName: string;
    clientIdentifier: string;
    currency?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AccountDecryptionService {
    private readonly secretKey: string;
    private cachedData: DecryptedAccountData | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig,
        sessionTeardown: SessionTeardownService,
        destroyRef: DestroyRef,
        private readonly logger: SafeLogger,
    ) {
        this.secretKey = environmentConfig.localStorageEncryptionKey;
        // Decrypted account data is PII (balance, fundShareClassCode, client id).
        // Clear it on session teardown so it doesn't survive logout on a shared
        // device. The cache also clears whenever this service is itself destroyed.
        sessionTeardown.cleared$
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => this.clearCache());
    }

    clearCache(): void {
        this.cachedData = null;
    }

    decryptAccountId(encryptedId: string): DecryptedAccountData {
        const decoded = decodeURIComponent(encryptedId);
        const decryptedBytes = CryptoJS.AES.decrypt(decoded, this.secretKey);
        const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
        const decryptedData = JSON.parse(decryptedString);
        this.cachedData = decryptedData;
        return decryptedData;
    }

    getDecryptedAccountData(): DecryptedAccountData | null {
        return this.cachedData;
    }

    getEncryptedAccountIdFromRoute(route: ActivatedRoute, currentUrl: string): string {
        let id: string | null = null;
        let currentRoute: ActivatedRoute | null = route;

        while (currentRoute && !id) {
            id = currentRoute.snapshot.paramMap.get('selectedAccount');
            currentRoute = currentRoute.parent;
        }

        if (!id) {
            const urlSegments = currentUrl.split('/');
            if (urlSegments.length >= 5) {
                let raw = urlSegments[4];

                try {
                    raw = decodeURIComponent(raw); // %252F -> %2F
                    raw = decodeURIComponent(raw); // %2F -> /
                } catch (e) {
                    this.logger.warn('Double decode failed', e);
                }

                id = raw;
            }
        }

        return id || '';
    }

    getSelectedAccountPath() {
        // Try direct access first
        let id = this.route.snapshot.paramMap.get('selectedAccount');

        // If null, try parent route
        if (!id && this.route.parent) {
            id = this.route.parent.snapshot.paramMap.get('selectedAccount');
        }

        // If still null, try root route segments
        if (!id) {
            const urlSegments = this.router.url.split('/');
            // The selectedAccountPath would be at index 4
            if (urlSegments.length >= 5) {
                id = urlSegments[4];
            }
        }

        return id;
    }

    getSavingsAccountSourceJourneyFromRoute(route: ActivatedRoute, currentUrl: string): string {
        let id: string | null = null;
        let currentRoute: ActivatedRoute | null = route;

        while (currentRoute && !id) {
            id = currentRoute.snapshot.paramMap.get('sourceJourney');
            currentRoute = currentRoute.parent;
        }

        if (!id) {
            const urlSegments = currentUrl.split('/');
            if (urlSegments.length >= 5) {
                let raw = urlSegments[4];

                try {
                    raw = decodeURIComponent(raw); // %252F -> %2F
                    raw = decodeURIComponent(raw); // %2F -> /
                } catch (e) {
                    this.logger.warn('Double decode failed', e);
                }

                id = raw;
            }
        }

        return id || '';
    }
}
